import { useCallback, useEffect, useRef } from 'react';
import { randomUUID } from '@/utilities/guid.utilities';

type ResizeCallback = (entry: ResizeObserverEntry) => void;
type CallbackRegistry = Map<string, ResizeCallback>;

interface ResizeObserverState {
  observer: ResizeObserver | null;
  callbacks: Map<Element, CallbackRegistry>;
  refCount: number;
}

const resizeObserverState: ResizeObserverState = {
  observer: null,
  callbacks: new Map<Element, CallbackRegistry>(),
  refCount: 0,
};

const initializeResizeObserver = (): ResizeObserver => {
  if (!resizeObserverState.observer) {
    resizeObserverState.observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const elementCallbacks = resizeObserverState.callbacks.get(entry.target);
        if (elementCallbacks) {
          elementCallbacks.forEach((callback) => callback(entry));
        }
      });
    });
  }
  return resizeObserverState.observer;
};

const cleanupResizeObserver = () => {
  if (resizeObserverState.refCount === 0 && resizeObserverState.observer) {
    resizeObserverState.observer.disconnect();
    resizeObserverState.observer = null;
    resizeObserverState.callbacks.clear();
  }
};

const generateHookId = randomUUID;

/**
 * Shared internal implementation for managing a single ResizeObserver instance across the app.
 *
 * This hook is **not meant to be used directly**. It provides imperative observer logic for `useResizeWatcher`,
 * and should not be consumed in application code or by other hooks. Use `useResizeWatcher` instead.
 */
export const useResizeObserver = () => {
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    resizeObserverState.refCount++;

    return () => {
      mountedRef.current = false;
      resizeObserverState.refCount--;
      // Ensure any synchronous React cleanup completes before disconnecting
      queueMicrotask(cleanupResizeObserver);
    };
  }, []);

  const observe = useCallback((element: Element, callback: ResizeCallback, id: string | null) => {
    if (!mountedRef.current) return () => {};

    const observer = initializeResizeObserver();
    const hookId = id || generateHookId();

    let elementCallbacks = resizeObserverState.callbacks.get(element);

    if (!elementCallbacks) {
      elementCallbacks = new Map();
      resizeObserverState.callbacks.set(element, elementCallbacks);
      observer.observe(element);
    }

    elementCallbacks.set(hookId, callback);

    return () => {
      const elementCallbacks = resizeObserverState.callbacks.get(element);
      if (elementCallbacks) {
        elementCallbacks.delete(hookId);
        if (elementCallbacks.size === 0) {
          resizeObserverState.callbacks.delete(element);
          observer.unobserve(element);
        }
      }
    };
  }, []);

  return { observe, generateHookId };
};
